import Link from 'next/link';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { cn } from '@/src/shared/lib/utils';
import { LanguageSelector } from '@/src/features/language-selector';
import { User, LayoutDashboard, Briefcase, ShoppingBag, Check } from 'lucide-react';
import { PrimaryButton } from '@/src/shared/ui/button';
import { ActorType } from '@hummii/shared-types';
import { useRole } from '@/src/shared/lib/hooks/use-role';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';
import { getUserActors, switchOrCreateActorByType } from '@/src/entities/user/api';
import { LogoutButton } from '@/src/features/auth';
import { mapRouteForActor } from '@/src/shared/lib/utils/route-mapper';

interface NavigationItem {
  label: string;
  href?: string;
  scrollTo?: string;
  onClick?: () => void;
}

interface Locale {
  code: string;
  label: string;
}

interface MobileMenuProps {
  isOpen: boolean;
  navItems: NavigationItem[];
  pathname: string;
  locale: string;
  availableLocales: Locale[];
  isAuthenticated: boolean;
  user?: {
    name?: string;
    email?: string;
  };
  activeRole?: string;
  activeSection?: string;
  onClose: () => void;
  onLocaleChange: (locale: string) => void;
  languageLabel: string;
  signInLabel: string;
}

export function MobileMenu({
  isOpen,
  navItems,
  pathname,
  locale,
  availableLocales,
  isAuthenticated,
  user,
  activeSection,
  onClose,
  onLocaleChange,
  languageLabel,
  signInLabel,
}: MobileMenuProps) {
  const { activeActorType } = useRole();
  const { setActiveActor, setAvailableActors } = useAuthStore();
  const router = useRouter();
  const [isSwitching, setIsSwitching] = useState(false);

  // Always show CLIENT and WORKER options
  const availableRoles = [ActorType.CLIENT, ActorType.WORKER];

  /**
   * Handle actor switch with smart route mapping
   * Creates actor if doesn't exist, then switches to it
   *
   * Uses mapRouteForActor which now:
   * - Preserves public pages (home, search, categories, profile)
   * - Preserves query params and hash (e.g., ?tab=orders)
   * - Maps actor-specific pages to their equivalents
   */
  const handleActorSwitch = async (actorType: ActorType) => {
    if (isSwitching) return;

    setIsSwitching(true);
    try {
      // Call backend API to switch/create actor
      const response = await switchOrCreateActorByType(actorType);

      // Update local state with new active actor
      setActiveActor(response.actor);

      // Refresh available actors from backend
      const { actors } = await getUserActors();
      setAvailableActors(actors);

      // Close mobile menu
      onClose();

      // Smart route mapping: preserve current page context and query params
      const targetPath = pathname
        ? mapRouteForActor(pathname, actorType)
        : `/${locale}/${actorType === ActorType.CLIENT ? 'client' : 'worker'}/dashboard`;

      console.log('Actor switched successfully:', {
        newActor: response.actor,
        currentPath: pathname,
        navigatingTo: targetPath,
      });

      // Navigate to appropriate page for new actor type
      router.push(targetPath);
    } catch (error) {
      console.error('Failed to switch actor:', error);
      alert('Failed to switch role. Please try again.');
    } finally {
      setIsSwitching(false);
    }
  };

  if (!isOpen) return null;

  const handleLocaleChange = (newLocale: string) => {
    onLocaleChange(newLocale);
    onClose();
  };

  const handleNavigationClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: NavigationItem
  ) => {
    if (item.onClick) {
      e.preventDefault();
      item.onClick();
      onClose();
    } else if (item.scrollTo) {
      e.preventDefault();
      const element = document.getElementById(item.scrollTo);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
      onClose();
    } else {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 desktop:hidden"
          onClick={onClose}
        />
      )}

      {/* Slide-in Menu */}
      <div
        className={cn(
          'fixed right-0 top-[60px] z-50 h-[calc(100vh-60px)] w-80 transform overflow-y-auto border-l border-gray-200 bg-white shadow-xl transition-transform duration-300 desktop:hidden',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="p-4">
          {/* Navigation Links */}
          <nav className="flex flex-col space-y-1">
              {navItems.map((item, index) => {
                // Check if this item is active based on section scroll or pathname
                const isActive = activeSection
                  ? item.scrollTo === activeSection
                  : pathname === item.href;

                // If onClick handler exists, render as button
                if (item.onClick) {
                  return (
                    <button
                      key={item.label || index}
                      onClick={() => {
                        item.onClick?.();
                        onClose();
                      }}
                      className={cn(
                        'rounded-lg px-4 py-3 text-mobile-body font-medium transition-colors text-left',
                        'bg-transparent border-none cursor-pointer w-full',
                        isActive
                          ? 'bg-accent-1/10 text-accent-1'
                          : 'text-text-primary hover:bg-background-2'
                      )}
                    >
                      {item.label}
                    </button>
                  );
                }

                // Otherwise, render as link
                return (
                  <Link
                    key={item.href || item.label || index}
                    href={item.href || '#'}
                    onClick={(e) => handleNavigationClick(e, item)}
                    className={cn(
                      'rounded-lg px-4 py-3 text-mobile-body font-medium transition-colors',
                      isActive
                        ? 'bg-accent-1/10 text-accent-1'
                        : 'text-text-primary hover:bg-background-2'
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
          </nav>

          {/* Language Selector Component */}
          <div className="mt-4 border-t border-gray-200 pt-4">
            <LanguageSelector
              currentLocale={locale}
              availableLocales={availableLocales}
              onLocaleChange={handleLocaleChange}
              label={languageLabel}
            />
          </div>

          {/* Auth Section (Mobile) */}
          <div className="mt-4 border-t border-gray-200 pt-4">
            {isAuthenticated ? (
              <div className="space-y-3">
                {/* User Info */}
                <div className="flex items-center gap-3 rounded-lg bg-background-2 p-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-1 text-white">
                    <User className="h-5 w-5" />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <div className="truncate text-mobile-body font-medium text-text-primary">
                      {user?.name}
                    </div>
                    <div className="truncate text-mobile-body-sm text-text-secondary">
                      {user?.email}
                    </div>
                  </div>
                </div>

                {/* Actor Switcher - Always shown for authenticated users */}
                <div className="space-y-2 mt-3">
                  <div className="text-mobile-body-sm font-medium text-text-secondary px-3">
                    Switch Role
                  </div>
                  {availableRoles.map((actorType) => {
                    const Icon = actorType === ActorType.WORKER ? Briefcase : ShoppingBag;
                    const isActive = (activeActorType ?? ActorType.CLIENT) === actorType;
                    const roleLabel = actorType === ActorType.WORKER ? 'Worker Mode' : 'Client Mode';

                    return (
                      <button
                        key={actorType}
                        onClick={() => handleActorSwitch(actorType)}
                        disabled={isSwitching}
                        className={cn(
                          'w-full flex items-center gap-3 rounded-lg px-3 py-2 transition-colors',
                          isActive
                            ? 'bg-accent-1/10'
                            : 'hover:bg-background-2',
                          isSwitching && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        <Icon className={cn('h-5 w-5', isActive ? 'text-accent-1' : 'text-text-secondary')} />
                        <span className={cn('text-mobile-body font-medium flex-1 text-left', isActive ? 'text-accent-1' : 'text-text-primary')}>
                          {roleLabel}
                        </span>
                        {isActive && <Check className="h-5 w-5 text-accent-1" />}
                      </button>
                    );
                  })}
                </div>

                <Link href={`/${locale}/dashboard`} onClick={onClose}>
                  <PrimaryButton>
                    <LayoutDashboard className="h-5 w-5" />
                    Dashboard
                  </PrimaryButton>
                </Link>

                <LogoutButton 
                  locale={locale} 
                  label="Logout" 
                  variant="primary"
                  onClick={onClose}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
